// Phase 5G — reusable workspace storage usage badge.
// Subtle customer-visible warning on export surfaces:
//   used_pct < 80  → hidden
//   80 ≤ pct < 95  → amber "approaching" warning (export still works)
//   pct ≥ 95       → red "critical" warning (export still works — the
//                    accepted EOS/renderer layer remains authoritative)
// Hard rule: NEVER blocks an export. Pure observability.
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { AlertTriangle, AlertCircle } from "lucide-react";

function fmtBytes(n) {
  if (!n) return "0 MB";
  if (n > 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n > 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

export default function StorageUsageBadge({ className = "" }) {
  const [u, setU] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/storage/usage`, { withCredentials: true })
      .then((r) => { if (alive) setU(r.data); })
      .catch(() => { /* 403 (role) or error → silently hidden. */ });
    return () => { alive = false; };
  }, []);

  if (!u || u.error || u.warning_level === "none") return null;

  const critical = u.warning_level === "critical";
  const Icon = critical ? AlertCircle : AlertTriangle;
  const color = critical ? "#b91c1c" : "#b45309";
  const bg = critical ? "rgba(220,38,38,0.08)" : "rgba(217,119,6,0.08)";
  const border = critical ? "rgba(220,38,38,0.25)" : "rgba(217,119,6,0.25)";

  return (
    <div
      className={`inline-flex items-start gap-2 text-[12px] px-3 py-2 rounded-md border ${className}`}
      style={{ color, background: bg, borderColor: border }}
      data-testid={`storage-usage-badge-${u.warning_level}`}
    >
      <Icon size={14} className="mt-0.5" />
      <div>
        <b>Storage {u.used_pct?.toFixed?.(0) ?? u.used_pct}% used</b>
        {" "}· {fmtBytes(u.used_bytes)} of {fmtBytes(u.quota_bytes)}.
        {" "}
        {critical
          ? "Clear space soon — exports still work for now, but the plan limit may stop them."
          : "You're approaching the plan limit. Exports still work normally."}
      </div>
    </div>
  );
}
